import BigNumber from 'bignumber.js'
import {
  type INFTContract,
  type ICampaignLanding,
  type ICoinInfo,
  type ITokenInfo,
  NFTTokenContentType,
  CampaignQuestType,
  CampaignAssetType,
  ICampaignDetail,
  YesNo,
  IQuestInfo,
} from 'oscar-typedef'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ID_TO_LOCALE, LOCALE_TO_ID, SHOW_RANKING_SIZE } from '@/const'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
// import { useUserAPIStore, useWalletStore } from '@/stores'
import { formattedDate } from '@/utils'

export enum NftMintingType {
  RANDOM = 1,
  SEQUENTIAL = 2,
  SAME = 3,
}
export interface IRewardAssetInfo {
  rewardId: string
  assetId: string
  assetType: CampaignAssetType
  coinInfo: ICoinInfo
  contractInfo: INFTContract | ITokenInfo | null
  amount: string
  totalAmount: string
  currentAmount: string
}
export interface CampaignInfomation {
  Campaign: ICampaignLanding
  rewardInfo: IRewardAssetInfo[]
}
export interface CampaignProcessInfo {
  id: number
  contentId: string
}

export interface AggregatedReward {
  assetId: string
  name: string
  symbol: string
  total: string // BigNumber string으로 정밀도 유지
  decimals: number
  contractAddress?: string
}
export interface RewardSummary {
  [CampaignAssetType.NFT]: Record<string, AggregatedReward>
  [CampaignAssetType.TOKEN]: Record<string, AggregatedReward>
  [CampaignAssetType.COIN]: Record<string, AggregatedReward>
}

/**
 * 다른 스토어들과 달리 API / Class 두개의 store 로 구분하지 않고 하나의 store로 구현
 */
export const useCampaignStore = defineStore('campaign', () => {
  const { locale } = useI18n()
  // const userAPIStore = useUserAPIStore()
  // const walletStore = useWalletStore()
  const campaign = ref<CampaignInfomation>()
  const campaignDetail = ref<ICampaignDetail>()
  const campaignLocaleId = ref(LOCALE_TO_ID[locale.value])
  const isFetched = ref(false)
  const hasNftAsset = ref(false)
  const processingInfo = ref<CampaignProcessInfo>({
    id: 0,
    contentId: '',
  })
  const isNftMovieClip = ref(false)
  // const userCampaignCondition = ref<IReferralEvent[]>()

  // ##########################################
  // Campaign
  // ##########################################
  const campaignId = ref('')
  const campaignDetailData = computed(() => {
    return (campaign.value?.Campaign as any).detail
  })
  const campaignStartTime = computed(() =>
    formattedDate(
      campaign.value?.Campaign.startTime ?? '',
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      undefined,
      ID_TO_LOCALE[campaignLocaleId.value ?? 1],
    ),
  )
  const campaignEndTime = computed(() =>
    formattedDate(
      campaign.value?.Campaign.endTime ?? '',
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      undefined,
      ID_TO_LOCALE[campaignLocaleId.value ?? 1],
    ),
  )
  const campaignNotStarted = computed(() => {
    const now = new Date().getTime()
    const startTime = new Date(
      campaign.value?.Campaign.startTime ?? '',
    ).getTime()
    return now < startTime
  })
  const campaignExpired = computed(() => {
    return campaign.value?.Campaign.isExpired ?? false
  })
  // ##########################################
  // Campaign Locale
  // ##########################################
  // const campaignLocaleId = computed(() => {
  //   // console.log('## localeId = ', localeId)
  //   console.log('## locale.value = ', locale.value)
  //   console.log('## LOCALE_TO_ID[locale.value] = ', LOCALE_TO_ID[locale.value])
  //   const localeId = (campaign.value?.Campaign.locales[0] as any).id
  //   if (localeId !== LOCALE_TO_ID[locale.value]) {
  //     locale.value = ID_TO_LOCALE[localeId]
  //   }
  //   return Number(LOCALE_TO_ID[locale.value])
  // })
  const campaignLocale = computed(() => {
    const locale = campaign.value?.Campaign.locales.find(
      (e: any) =>
        e.id === campaignLocaleId.value ||
        e.id === campaign.value?.Campaign.defaultLocaleId,
    )
    return locale
  })
  const campaignLocaleData = computed(() => {
    return campaignLocale.value?.data
  })

  // ##########################################
  // Campaign Qeust
  // ##########################################
  const campaignQuest = computed(() => {
    return campaign.value?.Campaign.quests
  })
  const campaignQuestData = computed(() => {
    return campaign.value?.Campaign.quests
  })
  const campaignLastQuest = computed(() => {
    const quests = campaign.value?.Campaign.quests
    return (
      quests?.find(
        (quest) => quest.mandatory === YesNo.YES && quest.isLast === YesNo.YES,
      ) ??
      quests?.find(
        (quest) => quest.mandatory === YesNo.NO && quest.isLast === YesNo.YES,
      )
    )
  })
  const campaignActionQuest = computed(() => {
    const quests = campaign.value?.Campaign.quests.filter(
      (quest) => quest.mandatory === YesNo.YES,
    )
    return quests
  })
  const campaignOptionalQuest = computed(() => {
    return campaign.value?.Campaign.quests.filter(
      (quest) => quest.mandatory === YesNo.NO,
    )
  })

  ////////////////////////////////////////////////////
  // TODO:
  // Invitation, Rankinig, LuckyDraw 같은 Quest에
  // 대한 계산 로직을 CampaignDetail.ts에 처리해야 되지 않나?
  // - Rank, extra reward
  ////////////////////////////////////////////////////
  const campaignInviteQuest = computed(() => {
    return campaign.value?.Campaign.quests.find(
      (quest) => quest.type === CampaignQuestType.Invite,
    )
  })
  const campaignInviteQuestDetail = computed(() => {
    if (!campaignInviteQuest.value) return []
    return campaignInviteQuest.value.locales.find(
      (e) =>
        e.localeId === campaignLocaleId.value ||
        e.localeId === campaign.value?.Campaign.defaultLocaleId,
    )?.data
  })
  const campaignRankingInviteQuest = computed(() => {
    return campaign.value?.Campaign.quests.find(
      (quest) => quest.type === CampaignQuestType.RankInvite,
    )
  })
  const campaignRankingInviteQuestDetail = computed(() => {
    if (!campaignRankingInviteQuest.value) return []
    return campaignRankingInviteQuest.value.locales.find(
      (e) =>
        e.localeId === campaignLocaleId.value ||
        e.localeId === campaign.value?.Campaign.defaultLocaleId,
    )?.data
  })
  const campaignLuckyDrawQuest = computed(() => {
    return campaign.value?.Campaign.quests.find(
      (quest) => quest.type === CampaignQuestType.LuckyDraw,
    )
  })
  const campaignLuckyDrawQuestDetail = computed(() => {
    if (!campaignLuckyDrawQuest.value) return []

    return campaignLuckyDrawQuest.value.locales.find(
      (e) =>
        e.localeId === campaignLocaleId.value ||
        e.localeId === campaign.value?.Campaign.defaultLocaleId,
    )?.data
    // const newVersion = (data as any).luckyDrawDetail
    // return newVersion ?? []
  })
  const campaignBonusQuest = computed(() => {
    // 보너스 미션의 locale 데이터를 통해 카드 형태 UI로 제공
    return campaign.value?.Campaign.quests.reduce(
      (acc: IQuestInfo[], quest) => {
        // quest 가 invite 또는 rank_invite 일 경우만 추가
        // 그런데 rank_invite quest 는 한개만 표시해줘야함 (안그러면 1위부터 3위까지 전부 화면에 표시됨..)
        if (
          (quest.type !== CampaignQuestType.Invite &&
            quest.type !== CampaignQuestType.RankInvite) ||
          (quest.type === CampaignQuestType.RankInvite &&
            acc.findIndex((q) => q.type === CampaignQuestType.RankInvite) !==
              -1)
        ) {
          return acc
        }
        acc.push(quest)
        return acc
      },
      [],
    )
  })
  const campaignGiveawayQuest = computed(() => {
    const quest = campaign.value?.Campaign.quests.find(
      (quest) =>
        quest.mandatory === YesNo.NO &&
        quest.type === CampaignQuestType.Giveaway,
    )
    return quest
  })
  const campaignGiveawayQuestDetail = computed(() => {
    if (!campaignGiveawayQuest.value) return []
    return campaignGiveawayQuest.value.locales.find(
      (e) =>
        e.localeId === campaignLocaleId.value ||
        e.localeId === campaign.value?.Campaign.defaultLocaleId,
    )?.data
  })

  // ##########################################
  // Campaign Reward
  // ##########################################
  const campaignReward = computed(() => {
    return campaignLastQuest.value?.rewards
  })
  const campaignRewardData = computed(() => {
    // 모든 퀘스트의 reward 는 마지막 퀘스트에 존재함.
    // detailView가 아닌 landingView 등 에서는 마지막 퀘스트의 reward 만 표시함.
    // detailView 에서는 모든 퀘스트를 순회하며 totalReward 를 계산함.
    return campaignLastQuest.value?.rewards
  })
  const campaignRandomReward = computed(() => {
    const randomReward = campaignLastQuest.value?.rewards?.find(
      (reward) => (reward.detail as any)?.random === true,
    )
    if (!randomReward) return null
    return randomReward.assets
  })
  const campaignAsset = computed(() => {
    return campaignReward.value?.flatMap((reward) => reward.assets)
  })
  const campaignRewardInfo = computed(() => {
    return campaign.value?.rewardInfo
  })
  const campaignRewardNetworkData = computed(() => {
    if (!campaign.value?.rewardInfo) return []

    const result = campaign.value?.rewardInfo?.map((reward) => {
      return {
        type: reward.assetType,
        network: reward.coinInfo.name,
        name:
          reward.assetType === CampaignAssetType.NFT ||
          reward.assetType === CampaignAssetType.TOKEN
            ? (reward.contractInfo as any)?.name
            : reward.coinInfo.name,
        symbol:
          reward.assetType === CampaignAssetType.NFT ||
          reward.assetType === CampaignAssetType.TOKEN
            ? (reward.contractInfo as any)?.symbol
            : reward.coinInfo.symbol,
        iconUrl:
          reward.assetType === CampaignAssetType.NFT
            ? (reward.contractInfo as any)?.imageUrl ??
              (reward.contractInfo as any)?.externalLink
            : reward.assetType === CampaignAssetType.TOKEN
              ? (reward.contractInfo as any)?.iconUrl ?? ''
              : reward.coinInfo.iconUrl || '',
        amount:
          reward.assetType === CampaignAssetType.NFT
            ? reward.amount
            : reward.assetType === CampaignAssetType.TOKEN
              ? Number(reward.amount) /
                Math.pow(10, (reward.contractInfo as any).decimals)
              : Number(reward.amount) /
                Math.pow(10, reward.coinInfo.decimals ?? 18),
      }
    })
    return result
  })

  // **Giveaway는 무조건 보상이 '1' 입니다.
  const campaignExtractedRewards = computed(() => {
    const rewards = campaign.value?.rewardInfo
    if (!rewards) return []
    // const result = _convertRewardArrayToObject(rewards)
    const result = _extractRewardInfo(rewards)
    if (campaignGiveawayQuest.value || campaignRandomReward.value) {
      return [result[0]]
    }
    return result
  })

  const campaignExpectedUserRewards = computed(() => {
    if (!campaignReward.value) return {}

    // AssetType 별로 그룹화
    const groupedByAssetType = _aggregateRewardsByAssetType(
      campaign.value?.rewardInfo as IRewardAssetInfo[],
    )
    const result = _formatRewardSummary(groupedByAssetType)
    return result
  })
  ////////////////////////////////////////////////////////////////////
  const campaignActionTotalReward = computed(() => {
    /**
     * TOTAL REWARD 계산
     */
    if (!campaignReward.value) return {}

    // 1. 모든 rewards를 하나의 배열로 평탄화
    const allRewards = campaignReward.value

    // 2. rewards를 assetType별로 그룹화
    const groupedByAssetType = allRewards.reduce(
      (assetGroups, reward) => {
        const assetType = reward.assetType
        if (!assetGroups[assetType]) {
          assetGroups[assetType] = []
        }
        assetGroups[assetType].push(reward)
        return assetGroups
      },
      {} as { [assetType: string]: typeof allRewards },
    )

    // 3. 각 assetType 그룹 내에서 id별로 그룹화하고 합산
    return Object.entries(groupedByAssetType).reduce(
      (result, [assetType, rewards]) => {
        // 3-1. 각 assetType의 rewards를 id별로 그룹화
        const groupedById = rewards.reduce(
          (idGroups, reward) => {
            const infoEntry = (reward.info as any)[0]
            const isNftOrToken =
              reward.assetType === CampaignAssetType.NFT ||
              reward.assetType === CampaignAssetType.TOKEN
            const id = isNftOrToken
              ? Number(infoEntry.contract.id)
              : Number(infoEntry.coin.id)
            if (!idGroups[id]) {
              idGroups[id] = {
                total: 0,
                symbol: isNftOrToken
                  ? infoEntry.contract.symbol
                  : infoEntry.coin.symbol,
                decimals: isNftOrToken
                  ? (infoEntry.contract.decimals ?? infoEntry.coin.decimals ?? 18)
                  : (infoEntry.coin.decimals ?? 18),
              }
            }

            // 3-2. decimals를 적용하여 실제 값으로 변환 후 합산
            const amount = reward.amount ?? '0'
            const actualAmount =
              Number(amount) / Math.pow(10, idGroups[id].decimals)
            idGroups[id].total += actualAmount

            return idGroups
          },
          {} as {
            [id: number]: { total: number; symbol: string; decimals: number }
          },
        )

        // 3-3. 결과 객체에 추가
        result[assetType as keyof typeof result] = groupedById
        return result
      },
      {} as {
        [key: string]: { [id: number]: { total: number; symbol: string } }
      },
    )
  })
  ////////////////////////////////////////////////////////////////////

  // Campaign Landing Composables
  const noUseRanking = computed(() => {
    return (
      campaign.value?.Campaign.quests.find(
        (quest) => quest.type === CampaignQuestType.RankInvite,
      ) === undefined
    )
  })

  const campaignRankingData = computed(() => {
    const rank = campaign.value?.Campaign.rank ?? []
    if (rank.length < SHOW_RANKING_SIZE) {
      return Array.from({ length: SHOW_RANKING_SIZE }).map(
        (_, idx) => rank?.[idx] ?? 0,
      )
    }
    return rank.slice(0, SHOW_RANKING_SIZE)
  })

  async function fetchCampaignInfo(
    id: string,
    force?: boolean,
  ): Promise<CampaignInfomation> {
    if (isFetched.value && !force) return campaign.value!
    campaignId.value = id

    const campaignResp =
      await WepinBackendAPI.getInstance().dropLink.getCampaignLanding(
        {
          campaignId: id,
        },
        {
          localeId: LOCALE_TO_ID[locale.value],
        },
      )
    if (!campaignResp) {
      throw new Error('Campaign is not exist')
    }

    const localeId = campaignResp.locales[0].id
    if (localeId !== LOCALE_TO_ID[locale.value]) {
      locale.value = ID_TO_LOCALE[localeId]
    }
    campaignLocaleId.value = LOCALE_TO_ID[locale.value]

    const currentTime: any = new Date()
    const startTime: any = new Date(campaignResp.startTime)
    if (startTime > currentTime) {
      throw new Error('Campaign has not started yet')
    }

    let lastQuest = campaignResp.quests.find(
      (quest: any) =>
        quest.mandatory === YesNo.YES && quest.isLast === YesNo.YES,
    )
    if (!lastQuest) {
      // throw new Error('Last quest is not exist')
      lastQuest = campaignResp.quests.find(
        (quest: any) =>
          quest.mandatory === YesNo.NO && quest.isLast === YesNo.YES,
      )
    }

    const rewards = lastQuest?.rewards
    const rewardInfo: any[] = []
    for (const reward of rewards ?? []) {
      let contractInfo: INFTContract | ITokenInfo | null = null
      let coinInfo: ICoinInfo | null = null
      if (reward.assets.length > 0) {
        const isMultiAsset = reward.assets.length > 1
        for await (const asset of reward.assets) {
          coinInfo = (reward.info as any)[0].coin
          if (asset.assetType === CampaignAssetType.NFT) {
            contractInfo = !isMultiAsset
              ? (reward.info as any)[0].contract ?? null
              : { ..._getAssetInfo(reward.info, asset.assetTypeId, false) }
            coinInfo = !isMultiAsset
              ? (reward.info as any)[0].coin
              : { ..._getAssetInfo(reward.info, asset.assetTypeId, true) }
            hasNftAsset.value = true
          } else if (asset.assetType === CampaignAssetType.TOKEN) {
            contractInfo = !isMultiAsset
              ? (reward.info as any)[0].contract ?? null
              : { ..._getAssetInfo(reward.info, asset.assetTypeId, false) }
            coinInfo = !isMultiAsset
              ? (reward.info as any)[0].coin
              : { ..._getAssetInfo(reward.info, asset.assetTypeId, true) }
          } else if (asset.assetType === CampaignAssetType.COIN) {
            contractInfo = null
            coinInfo = !isMultiAsset
              ? (reward.info as any)[0].coin
              : { ..._getAssetInfo(reward.info, asset.assetTypeId, true) }
          }
          if (!coinInfo) {
            throw new Error('Coin is not exist')
          }
          rewardInfo.push({
            rewardId: reward.id,
            assetId: asset.id,
            assetType: asset.assetType,
            coinInfo: coinInfo,
            contractInfo: contractInfo,
            amount: reward.amount.toString(),
            totalAmount: asset?.totalAmount.toString(),
            currentAmount: asset?.currentAmount.toString(),
          })
        }
      }
    }

    const campaignInfomation = {
      Campaign: campaignResp,
      rewardInfo: rewardInfo,
    }
    campaign.value = campaignInfomation
    isFetched.value = true
    return campaignInfomation
  }

  function _getAssetInfo(info: any, assetTypeId: number, isCoin: boolean) {
    const assetInfo = info.find((e: any) => e.contract.id === assetTypeId)
    return isCoin ? assetInfo.coin : assetInfo.contract
  }

  function getContentsInfo(localeId: number, assetId: string) {
    if (!campaign.value) throw new Error('Campaign is not exist')
    if (!isFetched.value) return

    const campaignData = campaign.value.Campaign
    const rewardInfo = campaign.value.rewardInfo.find(
      (e) => e.assetId === assetId,
    )

    const localeData = campaignData.locales.find(
      (e) => e.id === localeId || e.id === campaignData.defaultLocaleId,
    )
    if (!localeData) throw new Error('Campaign Locale content is not exist')

    if (rewardInfo?.assetType === CampaignAssetType.NFT) {
      const detail = campaignData.detail as any
      const useThumbnail =
        detail?.contentType === NFTTokenContentType.video &&
        detail?.thumbnail !== undefined &&
        detail?.thumbnail !== null
      const imageUrl = detail?.metadata?.image
      isNftMovieClip.value = useThumbnail
      return {
        imageUrl: imageUrl as string,
        contractName: rewardInfo?.contractInfo?.name,
        tokenName: detail?.metadata?.name as string,
        networkName: rewardInfo?.coinInfo?.name as string,
        mintingType: detail?.mintingType,
        contentMintingType: detail?.contentMintingType,
        contentType: detail?.contentType,
        iconUrl: rewardInfo?.coinInfo?.iconUrl as string,
        desc: detail?.metadata?.description as string,
        additionalUrl:
          detail?.metadata?.additionalUrl &&
          detail?.metadata?.additionalUrl.length >= 1
            ? (detail?.metadata?.additionalUrl[0]?.image as string)
            : null,
        thumbnail: detail?.thumbnail as string,
        useThumbnail: useThumbnail,
      }
    } else if (rewardInfo?.assetType === CampaignAssetType.TOKEN) {
      return rewardInfo?.contractInfo
    } else if (rewardInfo?.assetType === CampaignAssetType.COIN) {
      return rewardInfo?.coinInfo
    }
  }

  function _extractRewardInfo(rewards: IRewardAssetInfo[]) {
    const rewardInfo = rewards.map((reward) => {
      return {
        assetId: reward.assetId,
        type: reward.assetType,
        amount: reward.amount,
        name:
          reward.assetType === CampaignAssetType.NFT ||
          reward.assetType === CampaignAssetType.TOKEN
            ? (reward.contractInfo as any).name
            : reward.coinInfo?.name,
        symbol:
          reward.assetType === CampaignAssetType.NFT ||
          reward.assetType === CampaignAssetType.TOKEN
            ? (reward.contractInfo as any).symbol
            : reward.coinInfo?.symbol,
        decimals:
          reward.assetType === CampaignAssetType.NFT ||
          reward.assetType === CampaignAssetType.TOKEN
            ? (reward.contractInfo as any).decimals
            : reward.coinInfo?.decimals ?? 18,
      }
    })
    return rewardInfo
  }

  function _groupBy<T, K extends keyof T>(
    array: T[],
    key: K,
  ): Record<string, T[]> {
    return array.reduce(
      (groups, item) => {
        const group = String(item[key])
        groups[group] = groups[group] || []
        groups[group].push(item)
        return groups
      },
      {} as Record<string, T[]>,
    )
  }

  function _aggregateRewardsByAssetType(rewards: IRewardAssetInfo[]) {
    const result: RewardSummary = {
      [CampaignAssetType.NFT]: {},
      [CampaignAssetType.TOKEN]: {},
      [CampaignAssetType.COIN]: {},
    }

    // AssetType 별로 그룹화
    const groupedByType = _groupBy(rewards, 'assetType')
    // 각 AssetType 내에서 AssetTypeId 별로 집계
    Object.entries(groupedByType).forEach(([assetType, typeRewards]) => {
      const aggregated = _aggregateByAssetTypeId(typeRewards)
      result[assetType as CampaignAssetType] = aggregated
    })
    return result
  }

  function _aggregateByAssetTypeId(
    rewards: IRewardAssetInfo[],
  ): Record<string, AggregatedReward> {
    const grouped = _groupBy(rewards, 'assetId')

    return Object.entries(grouped).reduce(
      (acc, [assetId, assetRewards]) => {
        const firstReward = assetRewards[0]
        const decimals =
          firstReward.assetType === CampaignAssetType.NFT ||
          firstReward.assetType === CampaignAssetType.TOKEN
            ? (firstReward.contractInfo as any)?.decimals
            : firstReward.coinInfo.decimals ?? 18

        // BigNumber로 금액 합산
        const totalAmount = assetRewards.reduce((sum, reward) => {
          return sum.plus(new BigNumber(reward.amount || '0'))
        }, new BigNumber(0))
        const scale = new BigNumber(10).pow(decimals)
        const displayAmount = totalAmount.div(scale).toString()

        acc[assetId] = {
          assetId,
          name:
            firstReward.assetType === CampaignAssetType.NFT ||
            firstReward.assetType === CampaignAssetType.TOKEN
              ? (firstReward.contractInfo as any).name ?? ''
              : firstReward.coinInfo.name ?? '',
          symbol:
            firstReward.assetType === CampaignAssetType.NFT ||
            firstReward.assetType === CampaignAssetType.TOKEN
              ? (firstReward.contractInfo as any).symbol ?? ''
              : firstReward.coinInfo.symbol ?? '',
          total: displayAmount,
          decimals,
          contractAddress:
            firstReward.assetType === CampaignAssetType.NFT
              ? (firstReward.contractInfo as INFTContract).address
              : firstReward.assetType === CampaignAssetType.TOKEN
                ? (firstReward.contractInfo as ITokenInfo).contract
                : undefined,
        }
        return acc
      },
      {} as Record<string, AggregatedReward>,
    )
  }

  function _formatRewardSummary(summary: RewardSummary): any {
    const formatted: any = {}
    Object.entries(summary).forEach(([assetType, assets]) => {
      if (Object.keys(assets).length > 0) {
        formatted[assetType] = Object.entries(assets).reduce(
          (acc, [id, asset]) => {
            const typedAsset = asset as AggregatedReward
            acc[id] = {
              name: typedAsset.name,
              symbol: typedAsset.symbol,
              total: typedAsset.total,
              ...(typedAsset.contractAddress && {
                contractAddress: typedAsset.contractAddress,
              }),
            }
            return acc
          },
          {} as any,
        )
      }
    })
    return formatted
  }

  // async function getCamapingReferralList(id: string, userInfo: any) {
  //   const resp =
  //     await WepinBackendAPI.getInstance().dropLink.getReferralCodeCampaign(
  //       {
  //         campaignId: id,
  //       },
  //       {
  //         userId: userInfo.userId ?? undefined,
  //         email: userInfo.email ?? undefined,
  //       },
  //     )
  //   if (!resp) return
  //   userCampaignCondition.value = resp.event
  //   return resp
  // }

  async function sendCampaignRequestedEmail(
    campaignId: string,
    email: string,
    referralCode?: string,
  ) {
    const res = await WepinBackendAPI.getInstance().dropLink.requestCampaign(
      { campaignId },
      { email, localeId: campaignLocaleId.value },
    )
    if (!res) return
    const response =
      await WepinBackendAPI.getInstance().dropLink.requestEmailCampaign(
        { campaignId },
        {
          email,
          localeId: campaignLocaleId.value,
          referralCode: referralCode ?? undefined,
        },
      )
    if (!response.result) return
    return res
  }

  async function getCampaignDetail(id: string) {
    const campaignDetailResp =
      await WepinBackendAPI.getInstance().dropLink.getCampaignDetail(
        {
          campaignId: id,
        },
        {
          localeId: campaignLocaleId.value ?? 1,
        },
      )
    if (!campaignDetailResp) {
      throw new Error('Campaign detail is not exist')
    }
    campaignDetail.value = campaignDetailResp
    return campaignDetailResp
  }

  // function setProcessInfo(
  //   promotionInfo: PromotionInfomation,
  //   contentId: string
  // ) {
  //   processingInfo.value.id = promotionInfo.Promotion.id
  //   processingInfo.value.contentId = contentId
  // }

  // function getProcessInfo() {
  //   return processingInfo.value
  // }

  function reset() {
    campaign.value = undefined
    processingInfo.value = {
      id: 0,
      contentId: '',
    }
    // userCampaignCondition.value = []
  }

  return {
    campaign,
    campaignDetailData,
    campaignStartTime,
    campaignEndTime,
    campaignExpired,
    campaignNotStarted,
    campaignLocaleId,
    campaignLocale,
    campaignLocaleData,
    campaignQuest,
    campaignQuestData,
    campaignLastQuest,
    campaignActionQuest,
    campaignOptionalQuest,
    campaignInviteQuest,
    campaignInviteQuestDetail,
    campaignRankingInviteQuest,
    campaignRankingInviteQuestDetail,
    campaignLuckyDrawQuest,
    campaignLuckyDrawQuestDetail,
    campaignBonusQuest,
    campaignGiveawayQuest,
    campaignGiveawayQuestDetail,
    campaignReward,
    campaignRewardData,
    campaignRandomReward,
    campaignAsset,
    campaignRewardInfo,
    campaignRewardNetworkData,
    campaignExtractedRewards,
    campaignExpectedUserRewards,
    campaignActionTotalReward,
    // userCampaignCondition,
    noUseRanking,
    campaignRankingData,
    hasNftAsset,
    isNftMovieClip,
    fetchCampaignInfo,
    getContentsInfo,
    getCampaignDetail,
    sendCampaignRequestedEmail,
    reset,
    // setProcessInfo,
    // getProcessInfo,
  }
})
